import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../middleware/ThemeContext';
import StyledText from '../StyledComponents/StyledText';
import DataTable from '../listing/DataTable';
import Pagination from '../listing/Pagination';
import { Spinner } from 'react-bootstrap';
import { Theme } from '../../utils/themes';
import SearchIcon from '../../assets/icons/SearchIcon';
import { StyledInput } from '../listing/ExpandableSearch';
import SettingsIcon from '../../assets/icons/SettingsIcon';
import ChevronDownIcon from '../../assets/icons/ChevronDownIcon';
import DeleteIcon from '../../assets/icons/DeleteIcon';
import SyncIcon from '../../assets/icons/SyncIcon';
import LoadingCircleIcon from '../../assets/icons/LoadingCircleIcon';
import { useToast } from '../toast/ToastContext';
import { DropResult } from '@hello-pangea/dnd';
import AssignPrivateNoteModal from '../../features/searchQuery/AssignPrivateNoteModal';
import AssignContactRoleModal from '../../features/searchQuery/AssignContactRoleModal';
import DeletePrompt from '../DeletePrompt';
import BulkUpdateModal from '../../features/searchQuery/BulkUpdateModal';
import { useBulkDeleteMutation, useRunQueryMutation } from '../../api/searchQueryApiSlice';
import { hexToRgba, formatLabel } from '../../utils/helper';

interface SearchQueryRendererProps {
    searchQueryId: string;
    scope: string;
    searchQueryName?: string;
    showPagination?: boolean;
}

interface TableConfig {
    columnOrder: string[];
    visibleColumns: string[];
    sort: any;
    perPage: number;
}

export const SearchQueryRenderer: React.FC<SearchQueryRendererProps> = ({
    searchQueryId,
    scope,
    searchQueryName,
    showPagination = false,
}) => {
    const { theme } = useTheme();
    const showToast = useToast();
    const formatRef = useRef(null);

    // State management
    const [tableData, setTableData] = useState<any[]>([]);
    const [tableColumns, setTableColumns] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [formatOptions, setFormatOptions] = useState(false);
    const [resultSelectedIds, setResultSelectedIds] = useState<number[]>([]);
    const [contactRolesModal, setContactRolesModal] = useState(false);
    const [privateNoteModal, setPrivateNoteModal] = useState(false);
    const [showDeleteResults, setShowDeleteResults] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
    const [exportingFormat, setExportingFormat] = useState<string | null>(null);

    const [tableConfig, setTableConfig] = useState<TableConfig>({
        columnOrder: [],
        visibleColumns: [],
        sort: {},
        perPage: 25,
    });

    const [runResult, setRunResult] = useState({
        page: 1,
        total: 0,
        perPage: 10,
        totalPages: 0,
        query: '',
    });

    // Track the current request to avoid processing stale responses
    const requestIdRef = useRef<string>(`${searchQueryId}-${scope}`);
    const requestSeqRef = useRef(0);

    const [bulkDelete, { isLoading: bulkDeleteLoading }] = useBulkDeleteMutation();

    // RTK Query mutation for running search query
    const [runQuery, { data: apiResponse, isLoading, error }] = useRunQueryMutation();

    const fileFormats = [
        { label: 'Excel (unformatted)', value: 'EXCEL' },
        { label: 'Excel (formatted)', value: 'EXCEL formatted' },
        { label: 'CSV', value: 'CSV' },
        { label: 'JSON', value: 'JSON' },
        ...(scope !== 'organization'
            ? [
                { label: 'Assign contact role', value: 'contact role' },
                { label: 'Add private note', value: 'private note' },
            ]
            : []),
    ];

    // Clear table state when searchQueryId or scope changes to prevent stale data
    useEffect(() => {        
        // Update request ID to track which response is current
        requestIdRef.current = `${searchQueryId}-${scope}`;
        
        setTableData([]);
        setTableColumns([]);
        setTableConfig({
            columnOrder: [],
            visibleColumns: [],
            sort: {},
            perPage: 25,
        });
        setRunResult((prev) => ({
            ...prev,
            page: 1,
            total: 0,
            totalPages: 0,
            query: '',
        }));
        setResultSelectedIds([]);
    }, [searchQueryId, scope]);

    // Trigger runQuery mutation when dependencies change
    useEffect(() => {
        if (!searchQueryId || !scope) {
            console.warn('SearchQueryRenderer: Missing required props', { searchQueryId, scope });
            setLoading(false);
            setTableData([]);
            setTableColumns([]);
            return;
        }

        const currentRequestSeq = ++requestSeqRef.current;
        setTableData([]);
        setTableColumns([]);
        setLoading(true);

        runQuery({
            id: searchQueryId,
            page: runResult.page,
            perPage: runResult.perPage,
            query: runResult.query,
        })
            .unwrap()
            .catch(() => {
                // Error handling is centralized in the response effect via `error` state
            })
            .finally(() => {
                if (currentRequestSeq === requestSeqRef.current) {
                    setLoading(false);
                }
            });
    }, [searchQueryId, scope, runResult.page, runResult.perPage, runResult.query, runQuery]);

    // Process API response when it changes
    useEffect(() => {
        if (error) {
            console.error('Error fetching search query results:', error);
            setTableData([]);
            setTableColumns([]);
            showToast('Error', 'Failed to fetch search query results', 'error');
            return;
        }

        if (!apiResponse) {
            setTableData([]);
            setTableColumns([]);
            return;
        }

        // Verify response belongs to current request (not a stale response from previous scope)
        const currentRequestId = `${searchQueryId}-${scope}`;
        if (requestIdRef.current !== currentRequestId) {
            return;
        }
        // Process table data - get results
        // Handle multiple possible response structures
        let results = [];

        // Check all possible response structures
        if (Array.isArray(apiResponse)) {
            results = apiResponse;
        } else if (apiResponse?.results && Array.isArray(apiResponse.results)) {
            results = apiResponse.results;
        } else if (apiResponse?.data && Array.isArray(apiResponse.data)) {
            results = apiResponse.data;
        } else if (apiResponse?.items && Array.isArray(apiResponse.items)) {
            results = apiResponse.items;
        } else if (apiResponse && typeof apiResponse === 'object') {
            // Try to find any array property in the response
            const arrayKey = Object.keys(apiResponse).find(key => Array.isArray(apiResponse[key]));
            if (arrayKey) {
                results = apiResponse[arrayKey];
            } else {
                console.warn('Response is object but contains no array properties:', Object.keys(apiResponse));
            }
        }

        setTableData(results);

        // Generate columns from the first result object (similar to QueryCreation)
        let columns: any[] = [];
        if (results.length > 0) {
            const sampleRow = results[0];
            columns = Object.keys(sampleRow).map((key) => ({
                id: key,
                label: formatLabel(key),
                isSortable: true,
            }));
        } else if (apiResponse?.columns) {
            // Fallback to API columns if provided
            columns = apiResponse.columns;
        } else {
            console.warn('No results and no API columns provided');
        }

        setTableColumns(columns);

        // Update pagination info from API response
        const totalItems = apiResponse?.total || results.length || 0;
        const perPage = runResult.perPage;
        const calculatedTotalPages = perPage > 0 ? Math.ceil(totalItems / perPage) : 0;

        setRunResult((prev) => ({
            ...prev,
            total: totalItems,
            totalPages: calculatedTotalPages,
        }));

        // Initialize table config if empty
        if (tableConfig.columnOrder.length === 0 && columns.length > 0) {
            const columnIds = columns.map((col: any) => col.id || col.key);
            setTableConfig((prev) => ({
                ...prev,
                columnOrder: columnIds,
                visibleColumns: columnIds,
                perPage: runResult.perPage,
            }));
        }
    }, [apiResponse, error, searchQueryId, scope, runResult.perPage, showToast, tableConfig.columnOrder.length]);

    // Handle drag and drop for column reordering
    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) {
                return;
            }

            const newColumnOrder = Array.from(tableConfig.columnOrder);
            const [removed] = newColumnOrder.splice(result.source.index, 1);
            newColumnOrder.splice(result.destination.index, 0, removed);

            setTableConfig((prev) => ({
                ...prev,
                columnOrder: newColumnOrder,
            }));
        },
        [tableConfig.columnOrder]
    );

    // Handle select all
    const handleSelectAll = useCallback(() => {
        const normalizedData = tableData?.map((item) => ({
            ...item,
            id: typeof item.id === 'string' ? item.id : String(item.id),
        }));

        const allIds = normalizedData
            ?.map((item) => Number(item?.id))
            .filter((id) => !isNaN(id));

        const areAllSelected = allIds.every((id) => resultSelectedIds.includes(id));

        if (areAllSelected) {
            setResultSelectedIds([]);
        } else {
            setResultSelectedIds(allIds);
        }
    }, [tableData, resultSelectedIds]);

    // Handle export
    const exportFiles = async (value: string) => {
        if (exportingFormat) return;

        setExportingFormat(value);

        try {
            const token = localStorage.getItem('token');
            const exportUrl =
                value === 'contact role' || value === 'private note'
                    ? '#'
                    : `/api/search-query/${searchQueryId}/export?format=${value}&scope=${scope}`;

            const response = await fetch(exportUrl, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Export failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `export.${value.toLowerCase()}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast('Success', 'Data exported successfully', 'success');
        } catch (error) {
            console.error('Export error:', error);
            showToast('Error', 'Failed to export data', 'error');
        } finally {
            setExportingFormat(null);
        }
    };

    // Handle row actions
    const handleAction = useCallback(
        (action: string, id: string) => {
            switch (action) {
                case 'view':
                    // Navigate to detail view if needed
                    break;
                case 'delete':
                    setDeleteId(id);
                    setShowDeleteResults(true);
                    break;
                default:
                    break;
            }
        },
        []
    );

    // Handle delete
    const confirmDeleteResults = async () => {
        try {
            const { data }: any = await bulkDelete({
                recordIds: deleteId ? [deleteId] : resultSelectedIds,
                scope: scope,
            });

            if (data) {
                showToast('Success', 'Records deleted successfully', 'success');
                setShowDeleteResults(false);
                setDeleteId(null);
                setResultSelectedIds([]);
                // Refetch results
                setRunResult((prev) => ({ ...prev, page: 1 }));
            }
        } catch (error) {
            showToast('Error', 'Failed to delete records', 'error');
        }
    };

    return (
        <>
            {!searchQueryId || !scope ? (
                <PlaceholderContainer theme={theme}>
                    <StyledText fontSize="14px" color="#666">
                        Invalid search query configuration. Please save search query settings.
                    </StyledText>
                </PlaceholderContainer>
            ) : (
                <Container theme={theme}>
                    <HeaderSection theme={theme} className="d-flex align-items-center justify-content-between">
                        <StyledText
                            fontSize="16px"
                            fontWeight={700}
                            textColor={theme.CorporateTheme?.colors?.headerColor}
                        >
                            {searchQueryName || 'Search Query Results'}
                        </StyledText>
                    </HeaderSection>

                    {loading ? (
                        <LoadingContainer className="text-center p-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading results...</p>
                        </LoadingContainer>
                    ) : tableData.length === 0 ? (
                        <NoResultContainer theme={theme}>
                            <StyledText fontSize="16px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
                                No Result Found
                            </StyledText>
                            <StyledText fontSize="14px" textColor={theme.CorporateTheme.colors.textSecondary}>
                                Your search query returned no results
                            </StyledText>
                        </NoResultContainer>
                    ) : (
                        <>
                            <DataTable
                                noAction
                                hideIdColumn={true}
                                title={scope === 'contact' || scope === 'crm' ? 'Contacts Results' : scope === 'organization' ? 'Organizations Results' : 'Results'}
                                columns={tableColumns}
                                data={tableData || []}
                                columnOrder={tableConfig.columnOrder}
                                visibleColumns={tableConfig.visibleColumns}
                                sort={tableConfig.sort}
                                showCheckboxColumn={false}
                                noDataTitle="No Result Found"
                                noDataSubtitle="Your search query returned no results"
                                onDragEnd={handleDragEnd}
                                loading={loading}
                                perPage={tableConfig.perPage}
                                minWidth="100%"
                                containerHeight={360}
                                onAction={handleAction}
                                hideActionColumn
                            />

                            {showPagination && tableData?.length > 0 && (
                                <Pagination
                                    className="mt-3 py-1"
                                    currentPage={runResult?.page ?? 1}
                                    totalItems={runResult?.total ?? 0}
                                    perPage={runResult?.perPage ?? 25}
                                    totalPages={runResult?.totalPages ?? 0}
                                    onPageChange={(page: number) => {
                                        // Only change page if within valid range
                                        if (page >= 1 && page <= (runResult?.totalPages ?? 1)) {
                                            setRunResult((prev: any) => ({ ...prev, page }));
                                        }
                                    }}
                                    onLimitChange={(newLimit: number) => {
                                        // Update perPage and sync with tableConfig
                                        setRunResult((prev: any) => ({
                                            ...prev,
                                            perPage: newLimit,
                                            page: 1, // Reset to first page when limit changes
                                        }));
                                        setTableConfig((prev) => ({
                                            ...prev,
                                            perPage: newLimit,
                                        }));
                                    }}
                                />
                            )}
                        </>
                    )}
{/* 
                    {privateNoteModal && (
                        <AssignPrivateNoteModal
                            ids={resultSelectedIds.map(String)}
                            scope={scope ?? ''}
                            onClose={() => setPrivateNoteModal(false)}
                        />
                    )}

                    {contactRolesModal && (
                        <AssignContactRoleModal
                            ids={resultSelectedIds.map(String)}
                            refetch={() => setRunResult((prev) => ({ ...prev, page: 1 }))}
                            onClose={() => setContactRolesModal(false)}
                            setResultSelectedIds={(ids: string[]) =>
                                setResultSelectedIds(ids.map((id) => Number(id)))
                            }
                        />
                    )}

                    {showDeleteResults && (
                        <DeletePrompt
                            onConfirm={confirmDeleteResults}
                            onCancel={() => {
                                setShowDeleteResults(false);
                            }}
                            loading={bulkDeleteLoading}
                        />
                    )} */}
                </Container>
            )}
        </>
    );
};

const Container = styled.div<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 8px;
`;

const HeaderSection = styled.div<{ theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 4px;
`;

const ControlsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionWrapper = styled.div<{
    theme: Theme;
    removeBoxShadow?: boolean;
    height?: string;
    gap?: string;
    removeBorder?: boolean;
    borderRadius?: string;
    disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${({ gap }) => gap || '8px'};
  padding: 8px 12px;
  height: ${({ height }) => height || 'auto'};
  border: ${({ removeBorder, theme }) =>
        removeBorder ? 'none' : `1px solid ${theme.CorporateTheme.colors.border}`};
  border-radius: ${({ borderRadius }) => borderRadius || '4px'};
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  box-shadow: ${({ removeBoxShadow }) =>
        removeBoxShadow ? 'none' : '0 1px 3px rgba(0,0,0,0.1)'};
  cursor: pointer;
  position: relative;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:hover {
    background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.primary, 0.05)};
  }
`;

const FormatOptions = styled.div<{ theme: Theme }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 4px;
  min-width: 220px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 4px;

  hr {
    margin: 0;
    border: none;
    border-top: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  }
`;

const Options = styled.div<{ theme: Theme }>`
  padding: 12px 16px;
  color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  &:hover {
    background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.primary, 0.08)};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

const PlaceholderContainer = styled.div<{ theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background-color: #f8f9fa;
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 8px;
  min-height: 200px;
`;

const NoResultContainer = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 40px 20px;
    background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.border, 0.08)};
    border: 1px dashed ${({ theme }) => theme.CorporateTheme.colors.border};
    border-radius: 8px;
    min-height: 220px;
    text-align: center;
`;

export default SearchQueryRenderer;
